import { niceStep } from "@/lib/tool-kit/math/plot";

/**
 * Plotting geometry for functions of one variable: robust y-ranges, curve paths that
 * break at asymptotes and domain gaps, and zero / intersection / extremum finders.
 *
 * Everything is pure numbers so the graph components only draw.
 */

export type Curve = (x: number) => number;

export interface Window {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

const COORDINATE_LIMIT = 1e5;

function sampleXs(xMin: number, xMax: number, count: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i <= count; i += 1) {
    xs.push(xMin + ((xMax - xMin) * i) / count);
  }
  return xs;
}

/** The p-th quantile (0–1) of a sorted array. */
function quantile(sorted: number[], p: number): number {
  const at = (sorted.length - 1) * p;
  const lower = Math.floor(at);
  const upper = Math.ceil(at);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (at - lower);
}

/**
 * A y-range that shows the shape of the curves over [xMin, xMax]: it ignores the wild
 * tails near an asymptote (the 3rd–97th percentile of the finite samples), pads, keeps
 * the x-axis in view when it is close, and is never degenerate.
 */
export function autoYRange(
  curves: Curve[],
  xMin: number,
  xMax: number
): [number, number] {
  const values: number[] = [];
  for (const curve of curves) {
    for (const x of sampleXs(xMin, xMax, 400)) {
      const y = curve(x);
      if (Number.isFinite(y)) {
        values.push(y);
      }
    }
  }
  if (values.length === 0) {
    return [-5, 5];
  }
  values.sort((a, b) => a - b);
  let lo = quantile(values, 0.03);
  let hi = quantile(values, 0.97);
  if (values.length > 20) {
    // A smooth curve keeps its true extremes; only clip when the tails run away.
    const trueLo = values[0];
    const trueHi = values.at(-1) ?? hi;
    const core = hi - lo || 1;
    if (hi + core * 0.5 >= trueHi) {
      hi = trueHi;
    }
    if (lo - core * 0.5 <= trueLo) {
      lo = trueLo;
    }
  }
  if (hi - lo < 1e-9) {
    const centre = (hi + lo) / 2;
    const half = Math.max(1, Math.abs(centre) * 0.5);
    return [centre - half, centre + half];
  }
  const span = hi - lo;
  if (lo > 0 && lo < span) {
    lo = 0;
  } else if (hi < 0 && -hi < span) {
    hi = 0;
  }
  const pad = (hi - lo) * 0.1;
  return [lo - pad, hi + pad];
}

/** A tidy window: x as given, y chosen automatically. */
export function autoWindow(
  curves: Curve[],
  xMin: number,
  xMax: number
): Window {
  const [yMin, yMax] = autoYRange(curves, xMin, xMax);
  return { xMin, xMax, yMin, yMax };
}

/**
 * An SVG path through the curve, broken where it is undefined and across vertical
 * asymptotes (a sign change with a jump larger than the whole window).
 */
export function curvePath(
  f: Curve,
  window: Window,
  toX: (x: number) => number,
  toY: (y: number) => number,
  samples = 600
): string {
  const commands: string[] = [];
  const ySpan = window.yMax - window.yMin;
  let previous: number | null = null;
  let pen = false;
  for (const x of sampleXs(window.xMin, window.xMax, samples)) {
    const y = f(x);
    if (!Number.isFinite(y)) {
      pen = false;
      previous = null;
      continue;
    }
    const jumped =
      previous !== null &&
      Math.sign(y) !== Math.sign(previous) &&
      Math.abs(y - previous) > 2 * ySpan;
    if (jumped) {
      pen = false;
    }
    const px = toX(x);
    const py = Math.max(-COORDINATE_LIMIT, Math.min(COORDINATE_LIMIT, toY(y)));
    commands.push(`${pen ? "L" : "M"}${px.toFixed(2)} ${py.toFixed(2)}`);
    pen = true;
    previous = y;
  }
  return commands.join(" ");
}

/** Bisection on a bracket where f changes sign. */
export function bisect(f: Curve, a: number, b: number): number {
  let lo = a;
  let hi = b;
  let flo = f(lo);
  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    const fmid = f(mid);
    if (fmid === 0) {
      return mid;
    }
    if (Math.sign(fmid) === Math.sign(flo)) {
      lo = mid;
      flo = fmid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}

function goldenMinimum(f: Curve, a: number, b: number): number {
  const ratio = (Math.sqrt(5) - 1) / 2;
  let lo = a;
  let hi = b;
  let x1 = hi - ratio * (hi - lo);
  let x2 = lo + ratio * (hi - lo);
  let f1 = f(x1);
  let f2 = f(x2);
  for (let i = 0; i < 80; i += 1) {
    if (f1 < f2) {
      hi = x2;
      x2 = x1;
      f2 = f1;
      x1 = hi - ratio * (hi - lo);
      f1 = f(x1);
    } else {
      lo = x1;
      x1 = x2;
      f1 = f2;
      x2 = lo + ratio * (hi - lo);
      f2 = f(x2);
    }
  }
  return (lo + hi) / 2;
}

function dedupe(values: number[], tolerance: number): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const value of sorted) {
    if (out.length === 0 || value - (out.at(-1) as number) > tolerance) {
      out.push(value);
    }
  }
  return out;
}

function crossingZeros(f: Curve, xs: number[], ys: number[]): number[] {
  const found: number[] = [];
  for (let i = 0; i < xs.length - 1; i += 1) {
    const y0 = ys[i];
    const y1 = ys[i + 1];
    if (!(Number.isFinite(y0) && Number.isFinite(y1))) {
      continue;
    }
    if (y0 === 0) {
      found.push(xs[i]);
    } else if (Math.sign(y0) !== Math.sign(y1) && y1 !== 0) {
      const root = bisect(f, xs[i], xs[i + 1]);
      // |f| shrinks towards a zero, grows towards a pole, and stays put across a jump.
      if (Math.abs(f(root)) <= 1e-3 * Math.min(Math.abs(y0), Math.abs(y1))) {
        found.push(root);
      }
    }
  }
  if (ys.at(-1) === 0) {
    found.push(xs.at(-1) as number);
  }
  return found;
}

/** Zeros where f touches the axis without crossing, e.g. the double root of (x − 1)². */
function touchingZeros(f: Curve, xs: number[], ys: number[]): number[] {
  const found: number[] = [];
  const absolute = (x: number) => Math.abs(f(x));
  const scale = Math.max(1, ...ys.filter(Number.isFinite).map(Math.abs));
  for (let i = 1; i < xs.length - 1; i += 1) {
    const [p, q, r] = [ys[i - 1], ys[i], ys[i + 1]];
    const sameSign =
      [p, q, r].every(Number.isFinite) &&
      Math.sign(p) === Math.sign(r) &&
      Math.sign(q) === Math.sign(p);
    if (
      sameSign &&
      Math.abs(q) <= Math.abs(p) &&
      Math.abs(q) <= Math.abs(r) &&
      Math.abs(q) < 1e-2 * scale
    ) {
      const root = goldenMinimum(absolute, xs[i - 1], xs[i + 1]);
      if (absolute(root) < 1e-9 * scale) {
        found.push(root);
      }
    }
  }
  return found;
}

/**
 * Zeros of f on [a, b]: sign changes refined by bisection (poles rejected), plus
 * touching zeros found as minima of |f| that reach 0.
 */
export function findZeros(
  f: Curve,
  a: number,
  b: number,
  samples = 2000
): number[] {
  const xs = sampleXs(a, b, samples);
  const ys = xs.map(f);
  return dedupe(
    [...crossingZeros(f, xs, ys), ...touchingZeros(f, xs, ys)],
    (b - a) * 1e-7
  );
}

/** Points where two curves cross or touch. */
export function findIntersections(
  f: Curve,
  g: Curve,
  a: number,
  b: number
): { x: number; y: number }[] {
  return findZeros((x) => f(x) - g(x), a, b).map((x) => ({ x, y: f(x) }));
}

export type ExtremumKind = "max" | "min";

/** Local extrema from the zeros of the derivative, classified by its sign change. */
export function findExtrema(
  f: Curve,
  derivative: Curve,
  a: number,
  b: number
): { x: number; y: number; kind: ExtremumKind }[] {
  const step = (b - a) * 1e-5;
  const out: { x: number; y: number; kind: ExtremumKind }[] = [];
  for (const x of findZeros(derivative, a, b)) {
    const left = derivative(x - step);
    const right = derivative(x + step);
    if (left > 0 && right < 0) {
      out.push({ x, y: f(x), kind: "max" });
    } else if (left < 0 && right > 0) {
      out.push({ x, y: f(x), kind: "min" });
    }
  }
  return out;
}

/** Tick spacing the coordinate plane will use, exposed so a table can match the grid. */
export function gridStep(span: number): number {
  return niceStep(span, 8);
}
